import { useMemo } from 'react';
import useGetUserRecentSearches from 'apollo/userRecentSearch/useGetUserRecentSearches';
import {
    ContentSuggestionParams,
    Suggestions,
    SuggestionsType,
    isContentSuggestionParams,
    isFiltersParams,
} from '../types';
import getUserSearchFilterToFilterParams from '../helpers/getUserSearchFilterToFilterParams';
import { useLabels } from './useLabels';
import { usePacks } from './usePacks';

const useUserRecentSearchSuggestions = (
    showOnlyFilterSuggestions: boolean,
): Suggestions[] => {
    const userRecentSearches = useGetUserRecentSearches();

    const { labels } = useLabels();
    const { packs } = usePacks();

    const recentSearchSuggestion: Suggestions[] = useMemo(() => {
        return userRecentSearches
            .map((userRecentSearch): Suggestions => {
                const { filterSlug, searchFilter } = userRecentSearch;

                const params = getUserSearchFilterToFilterParams(searchFilter);

                if (params && isContentSuggestionParams(params)) {
                    const isLabel =
                        params === ContentSuggestionParams.suggestedlabels;
                    const content = isLabel
                        ? labels.find((label) => label.slug === filterSlug)
                        : packs.find((pack) => pack.slug === filterSlug);

                    if (content) {
                        const { artworkUrl, name, slug } = content;

                        return [
                            [params, { artworkUrl, name, slug }],
                            SuggestionsType.Recent,
                        ];
                    }
                } else if (params && isFiltersParams(params)) {
                    return [[params, filterSlug], SuggestionsType.Recent];
                }

                // this fallback will be filter out
                return [
                    [undefined],
                    SuggestionsType.Recent,
                ] as unknown as Suggestions;
            })
            .filter(([[filterParams]]) => !!filterParams)
            .filter(
                ([[filterParams]]) =>
                    (filterParams !== ContentSuggestionParams.suggestedlabels &&
                        filterParams !==
                            ContentSuggestionParams.suggestedpacks) ||
                    !showOnlyFilterSuggestions,
            )
            .slice(0, 3);
    }, [userRecentSearches, showOnlyFilterSuggestions, labels, packs]);

    return recentSearchSuggestion;
};

export default useUserRecentSearchSuggestions;
